import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Single / Duo / Trio offer selector.
 *
 * Product page: every row picks a size of the current product.
 * Hub page (<leggings-hub>): row 1 is the product chosen in the selector above.
 *   - default: the other rows pick colour and size within the same model
 *   - data-hub-models="true": the other rows also pick the model
 * All product data is read from the selector swatches already on the page.
 */

const hubMemory = new Map();
const norm = (value) => String(value ?? '').trim().toLowerCase();

class DuoSelector extends HTMLElement {
  #variants = [];
  #controller = null;
  #busy = false;
  #swatchData = new Map();
  #hiddenBuyButtons = null;
  #selectGeneration = 0;
  #resolved = null;

  connectedCallback() {
    this.#controller = new AbortController();

    const source = this.querySelector('[data-duo-variants]');
    try {
      this.#variants = JSON.parse(source?.textContent || '[]');
    } catch (error) {
      console.error('[duo-selector] invalid variant data', error);
      this.#variants = [];
    }

    this.percents = {
      duo: Number(this.dataset.percent) || 0,
      trio: Number(this.dataset.trioPercent) || 0,
    };
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';

    this.radios = Array.from(this.querySelectorAll('[data-duo-mode]'));
    this.cards = {};
    for (const radio of this.radios) this.cards[radio.value] = radio.closest('[data-duo-card]');

    this.rowsElement = this.querySelector('[data-duo-rows]');
    this.rowElements = Array.from(this.querySelectorAll('[data-duo-row]'));
    this.sizeSelects = this.rowElements.map((row) => row.querySelector('[data-duo-variant]'));
    this.colorSelects = this.rowElements.map((row) => row.querySelector('[data-duo-color]'));
    this.modelSelects = this.rowElements.map((row) => row.querySelector('[data-duo-model-select]'));
    this.fixedElements = this.rowElements.map((row) => row.querySelector('[data-duo-fixed]'));
    this.modelLabel = this.querySelector('[data-duo-model-label]');
    this.noteElement = this.querySelector('[data-duo-note]');
    this.addButton = this.querySelector('[data-duo-add]');
    this.errorElement = this.querySelector('[data-duo-error]');

    this.touched = this.rowElements.map(() => false);
    this.auto = this.rowElements.map(() => false);
    this.missing = this.rowElements.map(() => null);
    this.rowHandles = this.rowElements.map(() => null);
    this.totals = { duo: 0, trio: 0 };

    this.hub = this.closest('leggings-hub');
    this.multiModel = this.dataset.hubModels === 'true';
    this.sampler = this.dataset.trioSampler === 'true';
    this.isHub = Boolean(this.hub) && this.#setupHub();
    if (this.isHub) this.#restore();

    const { signal } = this.#controller;

    for (const radio of this.radios) {
      radio.addEventListener('change', () => this.#onModeChange(), { signal });
    }

    this.sizeSelects.forEach((select, index) => {
      select?.addEventListener(
        'change',
        () => {
          this.touched[index] = true;
          this.auto[index] = false;
          this.missing[index] = null;
          this.#update();
        },
        { signal }
      );
    });

    this.colorSelects.forEach((select, index) => {
      if (!select || index === 0) return;
      select.addEventListener('change', () => this.#onColorChange(index), { signal });
    });

    this.modelSelects.forEach((select, index) => {
      if (!select || index === 0) return;
      select.addEventListener('change', () => this.#onModelChange(index), { signal });
    });

    for (const card of this.querySelectorAll('[data-duo-card]')) {
      card.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('select, button, a')) return;
          const radio = card.querySelector('[data-duo-mode]');
          if (!radio || radio.checked) return;
          radio.checked = true;
          this.#onModeChange();
        },
        { signal }
      );
    }

    this.addButton?.addEventListener('click', () => this.#add(), { signal });
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    this.#render();
  }

  disconnectedCallback() {
    if (this.#hiddenBuyButtons) this.#hiddenBuyButtons.style.display = '';
    this.#hiddenBuyButtons = null;
    this.#controller?.abort();
  }

  /* ---------- variant selection from the theme picker ---------- */

  #onProductSelect = async (event) => {
    const generation = ++this.#selectGeneration;
    const inputBefore = this.#inputVariantId();
    let resolvedId = null;

    if (event?.promise) {
      try {
        const result = await event.promise;
        resolvedId = result?.detail?.resource?.id ?? null;
      } catch (error) {
        return;
      }
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));
    if (generation !== this.#selectGeneration || !this.isConnected) return;

    this.#resolved =
      resolvedId && this.#variant(resolvedId) ? { id: String(resolvedId), staleInput: inputBefore } : null;

    if (this.isHub) {
      const title = this.#singleVariant()?.title;
      for (let index = 1; index < this.rowElements.length; index++) {
        if (this.touched[index]) continue;
        if (this.auto[index]) this.#fillSizes(index, this.rowHandles[index], title);
        else this.#followFirstRow(index);
      }
      this.#render();
      return;
    }

    const id = this.#selectedVariantId();
    if (!id || !this.sizeSelects[0] || !this.#variant(id)) return;
    this.sizeSelects[0].value = String(id);
    this.#render();
  };

  #inputVariantId() {
    const scope = this.closest('.shopify-section') || document;
    const input = scope.querySelector('product-form-component input[name="id"]');
    return input?.value || null;
  }

  #selectedVariantId() {
    const fromInput = this.#inputVariantId();

    if (this.#resolved) {
      if (!fromInput || fromInput === this.#resolved.staleInput || fromInput === this.#resolved.id) {
        return this.#resolved.id;
      }
      this.#resolved = null;
    }

    return fromInput;
  }

  /* ---------- hub ---------- */

  #modelKey(swatch) {
    return `${swatch.dataset.waist}|${swatch.dataset.pocket}|${swatch.dataset.length}`;
  }

  #setupHub() {
    const current = this.#swatch(this.#currentHandle());
    if (!current) return false;

    const seen = new Set();
    this.allSwatches = Array.from(this.hub.querySelectorAll('[data-swatch]')).filter((swatch) => {
      const handle = swatch.dataset.handle;
      if (!handle || seen.has(handle)) return false;
      seen.add(handle);
      return true;
    });

    const currentKey = this.#modelKey(current);
    this.modelSwatches = this.allSwatches.filter((swatch) => this.#modelKey(swatch) === currentKey);
    if (!this.modelSwatches.length) return false;

    this.models = this.#buildModels(this.allSwatches);
    this.modelsByKey = new Map(this.models.map((model) => [model.key, model]));
    if (this.multiModel && this.models.length < 2) this.multiModel = false;

    this.dataset.layout = this.multiModel ? 'hub-models' : 'hub';

    if (this.modelLabel) {
      const label = this.modelsByKey.get(currentKey)?.label || '';
      this.modelLabel.textContent = label;
      this.modelLabel.hidden = this.multiModel || !label;
    }

    this.rowElements.forEach((row, index) => {
      if (index === 0) {
        if (this.sizeSelects[0]) this.sizeSelects[0].hidden = true;
        if (this.fixedElements[0]) this.fixedElements[0].hidden = false;
        return;
      }
      if (this.colorSelects[index]) this.colorSelects[index].hidden = false;
      if (this.modelSelects[index]) this.modelSelects[index].hidden = !this.multiModel;
    });

    return true;
  }

  #buildModels(swatches) {
    const pillValues = (filter) =>
      Array.from(this.hub.querySelectorAll(`[data-filter="${filter}"]`)).map((input) => input.value);
    const pillLabel = (filter, value) => {
      const input = this.hub.querySelector(`[data-filter="${filter}"][value="${CSS.escape(value || '')}"]`);
      return input?.parentElement?.querySelector('span')?.textContent?.trim() || '';
    };
    const rank = (list, value) => {
      const position = list.indexOf(value);
      return position === -1 ? 999 : position;
    };
    const order = { waist: pillValues('waist'), length: pillValues('length'), pocket: pillValues('pocket') };
    this.fabricOrder = order.waist.length
      ? order.waist
      : Array.from(new Set(swatches.map((swatch) => swatch.dataset.waist)));

    const map = new Map();
    for (const swatch of swatches) {
      const key = this.#modelKey(swatch);
      if (!map.has(key)) map.set(key, { key, swatch, swatches: [] });
      map.get(key).swatches.push(swatch);
    }

    const models = Array.from(map.values());
    const baseName = (swatch) => {
      const fabric = pillLabel('waist', swatch.dataset.waist);
      const length = swatch.dataset.length ? `${swatch.dataset.length}"` : '';
      const composed = [fabric, length].filter(Boolean).join(' ');
      return composed || swatch.dataset.model || swatch.dataset.handle;
    };

    const pocketValuesByName = new Map();
    for (const model of models) {
      const name = baseName(model.swatch);
      if (!pocketValuesByName.has(name)) pocketValuesByName.set(name, new Set());
      pocketValuesByName.get(name).add(model.swatch.dataset.pocket);
    }

    for (const model of models) {
      const name = baseName(model.swatch);
      const ambiguous = (pocketValuesByName.get(name)?.size || 0) > 1;
      const pocket = ambiguous ? pillLabel('pocket', model.swatch.dataset.pocket) : '';
      model.label = pocket ? `${name} · ${pocket}` : name;
      model.available = model.swatches.some((swatch) =>
        this.#swatchVariants(swatch.dataset.handle).some((variant) => variant.a)
      );
    }

    models.sort(
      (a, b) =>
        rank(order.waist, a.swatch.dataset.waist) - rank(order.waist, b.swatch.dataset.waist) ||
        rank(order.length, a.swatch.dataset.length) - rank(order.length, b.swatch.dataset.length) ||
        rank(order.pocket, a.swatch.dataset.pocket) - rank(order.pocket, b.swatch.dataset.pocket)
    );

    return models;
  }

  #modelOf(handle) {
    const swatch = this.#swatch(handle);
    return swatch ? this.modelsByKey.get(this.#modelKey(swatch)) : null;
  }

  #restore() {
    const saved = hubMemory.get(location.pathname);

    for (let index = 1; index < this.rowElements.length; index++) {
      const entry = saved?.rows?.[index];
      let handle = null;

      if (entry?.touched) {
        if (this.multiModel) {
          handle = this.#swatch(entry.handle) ? entry.handle : null;
        } else {
          const match = this.modelSwatches.find((swatch) => norm(swatch.dataset.color) === norm(entry.color));
          handle = match?.dataset.handle || null;
        }
      }

      if (handle) {
        this.touched[index] = true;
        this.#fillRow(index, handle, entry.size);
      } else {
        this.#followFirstRow(index);
      }
    }

    const radio = saved ? this.radios.find((item) => item.value === saved.mode) : null;
    if (radio) radio.checked = true;
    if (this.mode === 'trio') this.#applySampler();
  }

  #save() {
    if (!this.isHub) return;
    hubMemory.set(location.pathname, {
      mode: this.mode,
      rows: this.rowElements.map((row, index) => {
        if (index === 0) return null;
        const variant = this.#rowVariant(index);
        return {
          touched: this.touched[index],
          auto: this.auto[index],
          handle: this.rowHandles[index],
          color: this.#swatch(this.rowHandles[index])?.dataset.color || null,
          size: variant?.title || this.missing[index]?.size || null,
        };
      }),
    });
  }

  #fillRow(index, handle, wantedTitle) {
    if (this.multiModel) this.#fillModels(index, this.#modelOf(handle)?.key);
    this.#fillColors(index, handle);
    this.#fillSizes(index, handle, wantedTitle);
  }

  #followFirstRow(index) {
    this.touched[index] = false;
    this.auto[index] = false;
    this.#fillRow(index, this.#currentHandle(), this.#singleVariant()?.title);
    this.missing[index] = null;
  }

  #onModeChange() {
    if (this.mode === 'trio') this.#applySampler();
    else this.#clearSampler();
    this.#update();
  }

  #applySampler() {
    if (!this.isHub || !this.multiModel || !this.sampler) return;
    const current = this.#swatch(this.#currentHandle());
    if (!current) return;

    const used = new Set([current.dataset.waist]);
    for (let index = 1; index < this.rowElements.length; index++) {
      if (!this.touched[index]) continue;
      const waist = this.#swatch(this.rowHandles[index])?.dataset.waist;
      if (waist) used.add(waist);
    }

    const fabrics = this.fabricOrder.filter(
      (waist) => !used.has(waist) && this.models.some((model) => model.swatch.dataset.waist === waist && model.available)
    );

    for (let index = 1; index < this.rowElements.length; index++) {
      if (this.touched[index]) continue;
      const waist = fabrics.shift();
      const handle = waist ? this.#samplerHandle(waist, current) : null;
      if (!handle) {
        this.#followFirstRow(index);
        continue;
      }
      this.#fillRow(index, handle, this.#singleVariant()?.title);
      this.auto[index] = true;
    }
  }

  #clearSampler() {
    for (let index = 1; index < this.rowElements.length; index++) {
      if (this.auto[index]) this.#followFirstRow(index);
    }
  }

  #samplerHandle(waist, current) {
    const candidates = this.models.filter((model) => model.swatch.dataset.waist === waist && model.available);
    if (!candidates.length) return null;

    const same = (model, key) => model.swatch.dataset[key] === current.dataset[key];
    const model =
      candidates.find((item) => same(item, 'length') && same(item, 'pocket')) ||
      candidates.find((item) => same(item, 'length')) ||
      candidates.find((item) => same(item, 'pocket')) ||
      candidates[0];

    const size = norm(this.#singleVariant()?.title);
    const color = norm(current.dataset.color);
    const variantsOf = (swatch) => this.#swatchVariants(swatch.dataset.handle);
    const hasSize = (swatch) => variantsOf(swatch).some((variant) => variant.a && norm(variant.t) === size);
    const hasStock = (swatch) => variantsOf(swatch).some((variant) => variant.a);
    const sameColor = (swatch) => norm(swatch.dataset.color) === color;

    const swatch =
      model.swatches.find((item) => sameColor(item) && hasSize(item)) ||
      model.swatches.find(hasSize) ||
      model.swatches.find((item) => sameColor(item) && hasStock(item)) ||
      model.swatches.find(hasStock) ||
      model.swatches[0];

    return swatch?.dataset.handle || null;
  }

  #onModelChange(index) {
    const previous = this.#rowVariant(index);
    const previousColor = this.#swatch(this.rowHandles[index])?.dataset.color;
    const model = this.modelsByKey.get(this.modelSelects[index].value);
    if (!model) return;

    const hasStock = (swatch) => this.#swatchVariants(swatch.dataset.handle).some((variant) => variant.a);
    const target =
      model.swatches.find((swatch) => norm(swatch.dataset.color) === norm(previousColor) && hasStock(swatch)) ||
      model.swatches.find(hasStock) ||
      model.swatches[0];

    this.touched[index] = true;
    this.auto[index] = false;
    this.#fillColors(index, target.dataset.handle);
    this.#fillSizes(index, target.dataset.handle, previous?.title || this.missing[index]?.size);
    this.#update();
  }

  #onColorChange(index) {
    const previous = this.#rowVariant(index);
    const handle = this.colorSelects[index].value;
    this.rowHandles[index] = handle;
    this.touched[index] = true;
    this.auto[index] = false;
    this.#fillSizes(index, handle, previous?.title || this.missing[index]?.size);
    this.#update();
  }

  #fillModels(index, key) {
    const select = this.modelSelects[index];
    if (!select || !key) return;

    const soldOut = this.dataset.soldOutText || 'Sold out';
    select.replaceChildren(
      ...this.models.map((model) => {
        const option = new Option(model.available ? model.label : `${model.label} — ${soldOut}`, model.key);
        option.disabled = !model.available;
        return option;
      })
    );
    select.value = key;
  }

  #fillColors(index, handle) {
    this.rowHandles[index] = handle;
    const select = this.colorSelects[index];
    if (!select) return;

    const swatches = this.multiModel ? this.#modelOf(handle)?.swatches || [] : this.modelSwatches;
    const soldOut = this.dataset.soldOutText || 'Sold out';

    select.replaceChildren(
      ...swatches.map((swatch) => {
        const available = this.#swatchVariants(swatch.dataset.handle).some((variant) => variant.a);
        const label = swatch.dataset.color || swatch.dataset.handle;
        const option = new Option(available ? label : `${label} — ${soldOut}`, swatch.dataset.handle);
        option.disabled = !available;
        return option;
      })
    );
    select.value = handle;
  }

  #fillSizes(index, handle, wantedTitle) {
    const select = this.sizeSelects[index];
    if (!select) return;

    const soldOut = this.dataset.soldOutText || 'Sold out';
    const options = [new Option(this.dataset.chooseSizeText || 'Choose size', '')];
    let chosen = '';

    for (const variant of this.#swatchVariants(handle)) {
      const option = new Option(variant.a ? variant.t : `${variant.t} — ${soldOut}`, String(variant.id));
      option.disabled = !variant.a;
      options.push(option);
      if (!chosen && variant.a && wantedTitle && norm(variant.t) === norm(wantedTitle)) {
        chosen = String(variant.id);
      }
    }

    select.replaceChildren(...options);
    select.value = chosen;

    this.missing[index] = wantedTitle && !chosen ? { size: wantedTitle, color: this.#describe(handle) } : null;
  }

  #currentHandle() {
    return this.hub?.dataset.productHandle || null;
  }

  #swatch(handle) {
    if (!handle || !this.hub) return null;
    return this.hub.querySelector(`[data-swatch][data-handle="${CSS.escape(handle)}"]`);
  }

  #swatchVariants(handle) {
    if (!handle) return [];
    if (this.#swatchData.has(handle)) return this.#swatchData.get(handle);

    let list = [];
    try {
      list = JSON.parse(this.#swatch(handle)?.dataset.variants || '[]');
    } catch (error) {
      list = [];
    }

    this.#swatchData.set(handle, list);
    return list;
  }

  /* ---------- shared ---------- */

  get mode() {
    return this.radios.find((radio) => radio.checked)?.value || 'single';
  }

  #rowCount(mode) {
    return mode === 'trio' ? 3 : 2;
  }

  #variant(id) {
    return this.#variants.find((variant) => String(variant.id) === String(id));
  }

  #singleVariant() {
    return (
      this.#variant(this.#selectedVariantId()) ||
      this.#variants.find((variant) => variant.available) ||
      this.#variants[0]
    );
  }

  #rowVariant(index) {
    if (!this.isHub || index === 0) {
      const variant = this.isHub ? this.#singleVariant() : this.#variant(this.sizeSelects[index]?.value);
      if (!variant) return null;
      return {
        id: variant.id,
        title: variant.title,
        available: Boolean(variant.available),
        price: variant.price,
      };
    }

    const handle = this.rowHandles[index];
    const id = this.sizeSelects[index]?.value;
    if (!handle || !id) return null;

    const variant = this.#swatchVariants(handle).find((item) => String(item.id) === String(id));
    if (!variant) return null;

    return {
      id: variant.id,
      title: variant.t,
      available: Boolean(variant.a),
      price: typeof variant.p === 'number' ? variant.p : this.#singleVariant()?.price || 0,
    };
  }

  #describe(handle) {
    const color = this.#swatch(handle)?.dataset.color || '';
    if (!this.multiModel) return color;
    return [this.#modelOf(handle)?.label, color].filter(Boolean).join(' ');
  }

  #rowName(index) {
    if (!this.isHub) return this.dataset.productTitle || '';
    return this.#describe(index === 0 ? this.#currentHandle() : this.rowHandles[index]);
  }

  #rowColor(index) {
    if (!this.isHub) return this.dataset.productTitle || '';
    const handle = index === 0 ? this.#currentHandle() : this.rowHandles[index];
    return this.#swatch(handle)?.dataset.color || '';
  }

  #buyButtons() {
    const scope = this.closest('.shopify-section') || document;
    return scope.querySelector('.buy-buttons-block');
  }

  #hideBuyButtons() {
    const element = this.#buyButtons();
    if (!element) return;
    element.style.display = 'none';
    this.#hiddenBuyButtons = element;
  }

  #showBuyButtons() {
    const element = this.#hiddenBuyButtons || this.#buyButtons();
    if (element) element.style.display = '';
    this.#hiddenBuyButtons = null;
  }

  #money(cents) {
    return formatMoney(cents, this.moneyFormat, this.currency);
  }

  #setText(selector, value) {
    const element = this.querySelector(selector);
    if (element) element.textContent = value;
  }

  /* ---------- render ---------- */

  #update() {
    if (this.errorElement) this.errorElement.hidden = true;
    this.#render();
  }

  #render() {
    const mode = this.mode;
    this.dataset.mode = mode;

    for (const [value, card] of Object.entries(this.cards)) {
      card?.classList.toggle('is-selected', value === mode);
    }

    this.#placeRows(mode);
    this.#paintFirstRow();
    this.#paintSingle();
    this.#paintBundle('duo', 2);
    if (this.cards.trio) this.#paintBundle('trio', 3);

    const count = this.#rowCount(mode);
    const rows = Array.from({ length: count }, (_, index) => this.#rowVariant(index));
    const ready = rows.every((row) => row && row.available);

    if (mode === 'single') {
      this.#showBuyButtons();
      if (this.addButton) this.addButton.hidden = true;
    } else {
      this.#hideBuyButtons();
      if (this.addButton) {
        this.addButton.hidden = false;
        this.addButton.disabled = !ready || this.#busy;
        const text = (mode === 'trio' ? this.dataset.trioButtonText : this.dataset.duoButtonText) || '';
        const total = this.dataset.buttonTotal === 'true' && ready ? this.#money(this.totals[mode]) : '';
        const label = [text, total].filter(Boolean).join(' · ');
        if (label) this.addButton.textContent = label;
      }
    }

    this.#paintNotes(mode === 'single' ? 0 : count, rows);
    this.#save();
  }

  #placeRows(mode) {
    if (!this.rowsElement) return;

    const target = mode === 'trio' ? this.cards.trio : this.cards.duo;
    if (target && this.rowsElement.parentElement !== target) target.appendChild(this.rowsElement);

    const count = this.#rowCount(mode);
    this.rowElements.forEach((row, index) => {
      row.hidden = index >= count;
    });
    this.rowsElement.hidden = mode === 'single';
  }

  #paintFirstRow() {
    const fixed = this.fixedElements[0];
    if (!this.isHub || !fixed) return;

    const parts = [];
    if (this.multiModel) parts.push(this.#modelOf(this.#currentHandle())?.label || '');
    parts.push(this.#rowColor(0), this.#singleVariant()?.title || '');

    const main = document.createElement('span');
    main.textContent = parts.filter(Boolean).join(' · ');
    const nodes = [main];

    const above = this.dataset.aboveText || '';
    if (above) {
      const note = document.createElement('span');
      note.className = 'duo-selector__fixed-note';
      note.textContent = ` (${above})`;
      nodes.push(note);
    }
    fixed.replaceChildren(...nodes);
  }

  #paintSingle() {
    const single = this.#singleVariant();
    if (!single) return;

    this.#setText('[data-duo-single-price]', this.#money(single.price));
    const compare = this.querySelector('[data-duo-single-compare]');
    if (compare) {
      const show = single.compare_at_price > single.price;
      compare.textContent = show ? this.#money(single.compare_at_price) : '';
      compare.hidden = !show;
    }
  }

  #paintBundle(name, count) {
    const fallback = this.#rowVariant(0)?.price || 0;
    let full = 0;
    for (let index = 0; index < count; index++) {
      full += this.#rowVariant(index)?.price ?? fallback;
    }

    const percent = this.percents[name] || 0;
    const discounted = Math.round((full * (100 - percent)) / 100);

    this.totals[name] = discounted;
    this.#setText(`[data-${name}-price]`, this.#money(discounted));
    const compare = this.querySelector(`[data-${name}-compare]`);
    if (compare) {
      compare.textContent = this.#money(full);
      compare.hidden = full <= discounted;
    }

    const save = this.querySelector(`[data-${name}-save]`);
    if (save) {
      const template = this.dataset.saveText || '';
      const amount = full - discounted;
      save.textContent = template.replace('{amount}', this.#money(amount));
      save.hidden = !template || amount <= 0;
    }
  }

  #paintNotes(count, rows) {
    if (!this.noteElement) return;

    const messages = [];
    let needsSize = false;

    for (let index = 0; index < count; index++) {
      const row = rows[index];
      if (row && !row.available) {
        messages.push(this.#unavailableText(row.title, this.#rowName(index)));
      } else if (!row && this.missing[index]) {
        messages.push(this.#unavailableText(this.missing[index].size, this.missing[index].color));
      } else if (!row) {
        needsSize = true;
      }
    }

    if (needsSize && this.dataset.chooseSizeHint) messages.push(this.dataset.chooseSizeHint);

    this.noteElement.textContent = messages.join(' ');
    this.noteElement.hidden = messages.length === 0;
  }

  #unavailableText(size, color) {
    const template = this.dataset.sizeUnavailableText || 'Size {size} is not available in {color}.';
    return template.replace('{size}', size || '').replace('{color}', color || '');
  }

  #showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
  }

  /* ---------- add to cart ---------- */

  async #add() {
    if (this.#busy) return;

    const mode = this.mode;
    if (mode === 'single') return;

    const count = this.#rowCount(mode);
    const rows = Array.from({ length: count }, (_, index) => this.#rowVariant(index));
    if (!rows.every((row) => row && row.available)) {
      this.#render();
      return;
    }

    const counts = new Map();
    for (const row of rows) counts.set(row.id, (counts.get(row.id) || 0) + 1);
    const items = Array.from(counts, ([id, quantity]) => ({ id: Number(id), quantity }));

    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => element.dataset.sectionId)
      .filter(Boolean)
      .join(',');

    this.#busy = true;
    if (this.addButton) {
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
    }

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections }),
      });

      const data = await response.json();

      if (data.status) {
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'duo-selector',
          sourceId: this.dataset.productId || 'duo-selector',
          itemCount: count,
          productId: this.dataset.productId,
          sections: data.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error('[duo-selector]', error);
      deferred.reject(error);
      this.#showError(this.dataset.errorText || 'Something went wrong. Please try again.');
    } finally {
      this.#busy = false;
      if (this.addButton) {
        this.addButton.removeAttribute('aria-busy');
        this.addButton.disabled = false;
      }
      this.#render();
    }
  }
}

if (!customElements.get('duo-selector')) {
  customElements.define('duo-selector', DuoSelector);
}